import React, { useEffect, useState } from "react";
import Reveal from "./Reveal";
import { Calendar, Clock, Video } from "lucide-react";

const CALENDLY_URL = "https://calendly.com/ryan-2connect/30min";

export const BookCall = () => {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    // Inject Calendly stylesheet once
    if (!document.querySelector('link[href*="calendly.com/assets/external/widget.css"]')) {
      const link = document.createElement("link");
      link.href = "https://assets.calendly.com/assets/external/widget.css";
      link.rel = "stylesheet";
      document.head.appendChild(link);
    }
    // Inject Calendly widget script once
    const existing = document.querySelector(
      'script[src="https://assets.calendly.com/assets/external/widget.js"]'
    );
    if (existing) {
      setLoaded(true);
      return;
    }
    const script = document.createElement("script");
    script.src = "https://assets.calendly.com/assets/external/widget.js";
    script.async = true;
    script.onload = () => setLoaded(true);
    document.body.appendChild(script);
  }, []);

  const openPopup = () => {
    if (window.Calendly) {
      window.Calendly.initPopupWidget({ url: CALENDLY_URL });
    } else {
      window.open(CALENDLY_URL, "_blank", "noopener,noreferrer");
    }
    return false;
  };

  return (
    <section
      id="book"
      data-testid="book-call-section"
      className="relative py-28 lg:py-40 border-t border-gold-deep/40 z-10"
    >
      <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {/* Left: copy */}
          <div className="lg:col-span-5">
            <Reveal>
              <div className="overline mb-6 flex items-center gap-3">
                <Calendar className="w-3.5 h-3.5" />
                Book · 30-Minute Call
              </div>
              <h2 className="heading-section text-5xl lg:text-7xl text-white mb-8 leading-[0.95]">
                Get on Ryan's <span className="italic gold-gradient-text">calendar.</span>
              </h2>
              <p className="body-copy text-base max-w-md">
                Thirty focused minutes. No slides, no sales pitch — just a real conversation about
                what you're building and where I can help.
              </p>
            </Reveal>

            <Reveal delay={150}>
              <div className="mt-10 space-y-5">
                <div className="flex items-start gap-4" data-testid="book-detail-duration">
                  <Clock className="w-4 h-4 mt-1 text-gold shrink-0" />
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40">
                      Duration
                    </div>
                    <div className="font-serif text-xl text-white">30 minutes</div>
                  </div>
                </div>
                <div className="flex items-start gap-4" data-testid="book-detail-format">
                  <Video className="w-4 h-4 mt-1 text-gold shrink-0" />
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40">
                      Format
                    </div>
                    <div className="font-serif text-xl text-white">Video call · Zoom / Meet</div>
                  </div>
                </div>
                <div className="flex items-start gap-4" data-testid="book-detail-good-fits">
                  <Calendar className="w-4 h-4 mt-1 text-gold shrink-0" />
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40">
                      Good fits
                    </div>
                    <div className="font-serif text-xl text-white">
                      Partnerships · Pitches · Advice
                    </div>
                  </div>
                </div>
              </div>
            </Reveal>

            <Reveal delay={300}>
              <div className="mt-10 flex flex-wrap items-center gap-5">
                <button
                  type="button"
                  onClick={openPopup}
                  className="btn-primary"
                  data-testid="book-popup-cta"
                >
                  <Calendar className="w-4 h-4" />
                  Book a 30-min call
                </button>
                <a
                  href={CALENDLY_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/60 hover:text-gold transition-colors"
                  data-testid="book-external-link"
                >
                  Open in new tab →
                </a>
              </div>
            </Reveal>
          </div>

          {/* Right: inline embed */}
          <div className="lg:col-span-7">
            <Reveal delay={150}>
              <div
                className="relative border border-gold-deep/70 bg-[#080706] overflow-hidden"
                data-testid="book-calendly-embed"
              >
                <div className="absolute -top-3 -left-3 w-8 h-8 border-t border-l border-gold z-10" />
                <div className="absolute -bottom-3 -right-3 w-8 h-8 border-b border-r border-gold z-10" />

                <div className="flex items-center justify-between px-6 py-4 border-b border-gold-deep/60">
                  <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-gold/80">
                    Live Availability · Calendly
                  </span>
                  <span className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.28em] text-white/50">
                    <span className="w-1.5 h-1.5 rounded-full bg-gold animate-pulse" />
                    Updates in real time
                  </span>
                </div>

                <div
                  className="calendly-inline-widget"
                  data-url={`${CALENDLY_URL}?hide_event_type_details=0&hide_gdpr_banner=1&background_color=0a0a0a&text_color=fafafa&primary_color=d4af37`}
                  style={{ minWidth: "320px", height: "720px" }}
                />

                {!loaded && (
                  <div
                    className="absolute inset-x-0 bottom-0 h-[720px] flex items-center justify-center pointer-events-none"
                    aria-hidden="true"
                  >
                    <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40">
                      Loading calendar…
                    </div>
                  </div>
                )}
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BookCall;
